import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  MdChecklist,
  MdFileDownload,
  MdPeople,
  MdHourglassEmpty,
  MdTimelapse,
  MdSend,
  MdVerified,
  MdOpenInNew,
  MdFilterAlt,
  MdClear,
} from 'react-icons/md';
import api from '../services/api';
import toast from 'react-hot-toast';
import { resolveUserAvatarSrc } from '../utils/avatarUrl';
import './Assessment.css';

const API_URL =
  process.env.NODE_ENV === 'production'
    ? process.env.REACT_APP_API_URL || window.location.origin
    : ''; // relative paths go through CRA proxy → backend :6000

const statusLabel = {
  assigned: 'Not started',
  in_progress: 'In progress',
  submitted: 'Submitted',
  evaluated: 'Evaluated',
};

const exportToCsv = (filename, headers, rows) => {
  const encode = (val) => {
    if (val === null || val === undefined) return '';
    const str = String(val).replace(/"/g, '""');
    return `"${str}"`;
  };
  const csv = [headers, ...rows].map((r) => r.map(encode).join(',')).join('\r\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

const getInitials = (name) =>
  (name || '?')
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

const StaffAvatar = ({ user }) => {
  const [failed, setFailed] = useState(false);
  const imgSrc = useMemo(() => {
    if (failed || !user) return null;
    // Prefer relative /uploads path in dev (proxied); fall back to absolute API host
    const resolved = resolveUserAvatarSrc(user, API_URL);
    if (resolved) return resolved;
    // Last resort: if avatar is a bare filename, map to uploads path
    const raw = String(user.avatar || '').trim();
    if (!raw) return null;
    if (raw.startsWith('http') || raw.startsWith('/')) {
      return resolveUserAvatarSrc(user, API_URL || window.location.origin);
    }
    return `/uploads/avatars/${raw}`;
  }, [user, failed]);

  useEffect(() => {
    setFailed(false);
  }, [user?._id, user?.avatar]);

  return (
    <span
      className={`acr-avatar ${imgSrc ? 'has-img' : ''}`}
      aria-hidden
      title={user?.name || ''}
    >
      {imgSrc ? (
        <img
          src={imgSrc}
          alt=""
          className="acr-avatar-img"
          onError={() => setFailed(true)}
        />
      ) : (
        <span className="acr-avatar-initials">
          {getInitials(user?.name)}
        </span>
      )}
    </span>
  );
};

const AssessmentCompletionReport = () => {
  const [rows, setRows] = useState([]);
  const [userRoster, setUserRoster] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [assessmentId, setAssessmentId] = useState('');
  const [staffId, setStaffId] = useState('');
  const [status, setStatus] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const load = async (overrides = {}) => {
    setLoading(true);
    try {
      const nextStatus =
        overrides.status !== undefined ? overrides.status : status;
      const nextFrom = overrides.from !== undefined ? overrides.from : from;
      const nextTo = overrides.to !== undefined ? overrides.to : to;
      const params = {};
      if (nextFrom) params.from = nextFrom;
      if (nextTo) params.to = nextTo;
      if (nextStatus) params.status = nextStatus;
      const [{ data }, usersRes] = await Promise.all([
        api.get('/assessments/reports/completion', { params }),
        api.get('/users/list').catch(() => ({ data: { data: [] } })),
      ]);
      setRows(data.data || []);
      setSummary(data.summary || null);
      setUserRoster(usersRes.data?.data || []);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load report');
      setRows([]);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const assessmentOptions = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => {
      const id = r.assessment?._id;
      if (!id || map.has(String(id))) return;
      map.set(String(id), {
        _id: String(id),
        title: r.assessment?.title || 'Untitled',
      });
    });
    return [...map.values()].sort((a, b) => a.title.localeCompare(b.title));
  }, [rows]);

  const staffOptions = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => {
      const id = r.staff?._id;
      if (!id || map.has(String(id))) return;
      map.set(String(id), {
        _id: String(id),
        name: r.staff?.name || 'Unknown',
        email: r.staff?.email || '',
      });
    });
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [rows]);

  const filtered = useMemo(() => {
    const rosterById = new Map(
      userRoster.map((u) => [String(u._id || u.id), u]),
    );
    return rows
      .filter((r) => {
        if (
          assessmentId &&
          String(r.assessment?._id || '') !== String(assessmentId)
        ) {
          return false;
        }
        if (staffId && String(r.staff?._id || '') !== String(staffId)) {
          return false;
        }
        return true;
      })
      .map((r) => {
        if (!r.staff) return r;
        const match = rosterById.get(String(r.staff._id));
        if (!match) return r;
        return {
          ...r,
          staff: {
            ...r.staff,
            avatar: r.staff.avatar || match.avatar || '',
            name: r.staff.name || match.name,
            email: r.staff.email || match.email,
          },
        };
      });
  }, [rows, assessmentId, staffId, userRoster]);

  const hasActiveFilters =
    Boolean(assessmentId) ||
    Boolean(staffId) ||
    Boolean(status) ||
    Boolean(from) ||
    Boolean(to);

  const handleExport = () => {
    exportToCsv(
      'assessment-completion.csv',
      [
        'Assessment',
        'Staff',
        'Email',
        'Status',
        'Score',
        'Score %',
        'Started',
        'Submitted',
        'Evaluated',
        'Scheduled',
      ],
      filtered.map((r) => [
        r.assessment?.title,
        r.staff?.name,
        r.staff?.email,
        statusLabel[r.status] || r.status,
        r.totalScore != null && r.assessment?.totalMarks != null
          ? `${r.totalScore}/${r.assessment.totalMarks}`
          : '',
        r.scorePercent != null ? r.scorePercent : '',
        r.startedAt || '',
        r.submittedAt || '',
        r.evaluatedAt || '',
        r.assessment?.scheduledAt || '',
      ]),
    );
  };

  const clearFilters = () => {
    setAssessmentId('');
    setStaffId('');
    setStatus('');
    setFrom('');
    setTo('');
    load({ status: '', from: '', to: '' });
  };

  const statCards = summary
    ? [
        {
          key: 'assigned',
          label: 'Total assigned',
          value: summary.assigned,
          icon: MdPeople,
          tone: 'neutral',
        },
        {
          key: 'notStarted',
          label: 'Not started',
          value: summary.notStarted,
          icon: MdHourglassEmpty,
          tone: 'info',
        },
        {
          key: 'inProgress',
          label: 'In progress',
          value: summary.inProgress,
          icon: MdTimelapse,
          tone: 'warning',
        },
        {
          key: 'submitted',
          label: 'Submitted',
          value: summary.submitted,
          icon: MdSend,
          tone: 'primary',
        },
        {
          key: 'evaluated',
          label: 'Evaluated',
          value: summary.evaluated,
          icon: MdVerified,
          tone: 'success',
        },
      ]
    : [];

  return (
    <div className="assessment-page assessment-report-page">
      <div className="assessment-header">
        <div>
          <h1>
            <MdChecklist size={28} />
            Assessment Completion
          </h1>
          <p className="assessment-subtitle">
            Track attempt status for every assigned staff member — filter,
            review, and export results.
          </p>
        </div>
        <div className="assessment-actions">
          <button
            type="button"
            className="assessment-btn secondary"
            onClick={handleExport}
            disabled={!filtered.length}
          >
            <MdFileDownload size={18} />
            Export Excel
          </button>
        </div>
      </div>

      <div className="acr-filters">
        <div className="acr-filters-head">
          <span className="acr-filters-title">
            <MdFilterAlt size={18} />
            Filters
          </span>
          {hasActiveFilters && (
            <button
              type="button"
              className="assessment-btn ghost"
              onClick={clearFilters}
            >
              <MdClear size={16} />
              Clear all
            </button>
          )}
        </div>
        <div className="acr-filters-grid">
          <div className="assessment-field">
            <label>Assessment</label>
            <select
              value={assessmentId}
              onChange={(e) => setAssessmentId(e.target.value)}
            >
              <option value="">All assessments</option>
              {assessmentOptions.map((a) => (
                <option key={a._id} value={a._id}>
                  {a.title}
                </option>
              ))}
            </select>
          </div>
          <div className="assessment-field">
            <label>Staff</label>
            <select
              value={staffId}
              onChange={(e) => setStaffId(e.target.value)}
            >
              <option value="">All staff</option>
              {staffOptions.map((s) => (
                <option key={s._id} value={s._id}>
                  {s.name}
                  {s.email ? ` (${s.email})` : ''}
                </option>
              ))}
            </select>
          </div>
          <div className="assessment-field">
            <label>Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="">All statuses</option>
              <option value="assigned">Not started</option>
              <option value="in_progress">In progress</option>
              <option value="submitted">Submitted</option>
              <option value="evaluated">Evaluated</option>
            </select>
          </div>
          <div className="assessment-field">
            <label>From</label>
            <input
              type="date"
              value={from}
              max={to || undefined}
              onChange={(e) => setFrom(e.target.value)}
            />
          </div>
          <div className="assessment-field">
            <label>To</label>
            <input
              type="date"
              value={to}
              min={from || undefined}
              onChange={(e) => setTo(e.target.value)}
            />
          </div>
          <div className="acr-filters-actions">
            <button
              type="button"
              className="assessment-btn"
              onClick={() => load()}
            >
              Apply
            </button>
            <button
              type="button"
              className="assessment-btn secondary"
              onClick={clearFilters}
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      {summary && (
        <div className="acr-stats">
          {statCards.map((card) => {
            const Icon = card.icon;
            return (
              <div
                key={card.key}
                className={`acr-stat-card acr-stat-card--${card.tone}`}
              >
                <div className="acr-stat-icon">
                  <Icon size={20} />
                </div>
                <div>
                  <div className="acr-stat-label">{card.label}</div>
                  <div className="acr-stat-value">{card.value}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {loading ? (
        <div className="assessment-loading">
          <span className="spinner" /> Loading report…
        </div>
      ) : (
        <div className="acr-table-card">
          <div className="acr-table-head">
            <div>
              <h2>Attempts</h2>
              <p>
                Showing <strong>{filtered.length}</strong> of{' '}
                <strong>{rows.length}</strong> attempts
              </p>
            </div>
          </div>

          {!filtered.length ? (
            <div className="assessment-empty">No completion data found.</div>
          ) : (
            <div className="table-wrapper">
              <table className="acr-table">
                <thead>
                  <tr>
                    <th>Assessment</th>
                    <th>Staff</th>
                    <th>Status</th>
                    <th>Score</th>
                    <th>Started</th>
                    <th>Submitted</th>
                    <th>Scheduled</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => {
                    const pct = r.scorePercent;
                    const hasScore = ['submitted', 'evaluated'].includes(
                      r.status,
                    );
                    return (
                      <tr key={r.attemptId}>
                        <td>
                          {r.assessment?._id ? (
                            <Link
                              to={`/assessments/${r.assessment._id}/review`}
                              className="acr-assessment-link"
                            >
                              {r.assessment.title || 'Untitled'}
                            </Link>
                          ) : (
                            <span className="acr-muted">—</span>
                          )}
                        </td>
                        <td>
                          <div className="acr-staff-cell">
                            <StaffAvatar user={r.staff} />
                            <span>
                              <span className="acr-staff-name">
                                {r.staff?.name || '—'}
                              </span>
                              {r.staff?.email ? (
                                <span className="acr-staff-email">
                                  {r.staff.email}
                                </span>
                              ) : null}
                            </span>
                          </div>
                        </td>
                        <td>
                          <span
                            className={`assessment-badge ${r.status || ''}`}
                          >
                            {statusLabel[r.status] || r.status}
                          </span>
                        </td>
                        <td>
                          {hasScore ? (
                            <div className="acr-score-cell">
                              <span className="acr-score-main">
                                {r.totalScore ?? 0}
                                <small>/{r.assessment?.totalMarks ?? 0}</small>
                              </span>
                              {pct != null && (
                                <span
                                  className={`acr-score-pct ${
                                    pct >= 70
                                      ? 'high'
                                      : pct >= 40
                                        ? 'mid'
                                        : 'low'
                                  }`}
                                >
                                  {pct}%
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="acr-muted">—</span>
                          )}
                        </td>
                        <td>
                          <span className="acr-date">
                            {formatDate(r.startedAt)}
                          </span>
                        </td>
                        <td>
                          <span className="acr-date">
                            {formatDate(r.submittedAt)}
                          </span>
                        </td>
                        <td>
                          <span className="acr-date">
                            {formatDate(r.assessment?.scheduledAt)}
                          </span>
                        </td>
                        <td>
                          {r.assessment?._id && r.attemptId ? (
                            <Link
                              to={`/assessments/${r.assessment._id}/review/${r.attemptId}`}
                              className="assessment-btn secondary sm"
                            >
                              <MdOpenInNew size={16} />
                              View details
                            </Link>
                          ) : (
                            <span className="acr-muted">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AssessmentCompletionReport;
